/**
 * Main patient management container that orchestrates between patient list and detail views.
 * Fetches patient data from API and handles navigation between list and individual patient screens.
 */
import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useWindowDimensions, View, StyleSheet } from "react-native";

import { TClinicPatientType } from "../../../../utils/types";

import {
  InputDropdown,
  NormalText,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../../common/native";
import { SessionContext } from "../../../../contexts";
import PatientList from "./PatientList";
import {
  getPatients,
  getPatientsPaginated,
  TGetPatientsPaginatedResponse,
  TGetPatientsResponse,
  TPatientsPagination,
  TPatientInfo,
} from "../../../../services/api-clinic";
import { logUIError } from "../../../../services/api-misc";
import PatientDetail from "./PatientDetail";
import { buildConsultPatientFromClinicPatient } from "../consults/utils";

type PatientsProps = {};
const PATIENTS_PAGE_LIMIT = 50;
const LEAD_LOOKUP_PAGE_LIMIT = 200;
const MAX_LEAD_LOOKUP_PAGES = 25;
const LEAD_EXPORT_PAGE_LIMIT = 200;
const MAX_LEAD_EXPORT_PAGES = 100;

const resolveLeadTimestamp = (lead: TPatientInfo) => {
  const createdAt = Date.parse(lead.createdAt || "");
  if (!Number.isNaN(createdAt)) return createdAt;

  const updatedAt = Date.parse(lead.updatedAt || "");
  if (!Number.isNaN(updatedAt)) return updatedAt;

  return Number.POSITIVE_INFINITY;
};

const isOlderLead = (candidate: TPatientInfo, current: TPatientInfo) => {
  const candidateTime = resolveLeadTimestamp(candidate);
  const currentTime = resolveLeadTimestamp(current);

  if (candidateTime !== currentTime) return candidateTime < currentTime;
  return (candidate.uuid || "") < (current.uuid || "");
};

const escapeCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

const formatCsvProcedures = (procedures?: string[]) => {
  if (!procedures || procedures.length === 0) return "";
  return procedures.join("; ");
};

const Patients: React.FC<PatientsProps> = ({}) => {
  const navigate = useNavigate();
  const session = React.useContext(SessionContext)?.session;
  const isFreePlan = session?.planType === "FREE";
  const [hasFetchedPatients, setHasFetchedPatients] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const patientId = searchParams.get("patientId");
  const leadId = searchParams.get("leadId");

  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;
  const [isErrorGettingPatients, setIsErrorGettingPatients] =
    React.useState<boolean>(false);

  const [patients, setPatients] = React.useState<TPatientInfo[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState("");
  const [patientsPagination, setPatientsPagination] =
    React.useState<TPatientsPagination | null>(null);
  const [unlockedFreeLeadUuid, setUnlockedFreeLeadUuid] = React.useState<
    string | null
  >(null);
  const [waitingLeadsCount, setWaitingLeadsCount] = React.useState(0);
  const [isExportingLeadsCsv, setIsExportingLeadsCsv] = React.useState(false);
  const [exportLeadsCsvError, setExportLeadsCsvError] = React.useState<
    string | null
  >(null);
  const [currentCursor, setCurrentCursor] = React.useState<string | undefined>(
    undefined,
  );
  const [cursorHistory, setCursorHistory] = React.useState<
    Array<string | undefined>
  >([]);
  const [patientDetail, setPatientDetail] = React.useState<TPatientInfo | null>(
    null,
  );

  const filterOptions = React.useMemo(
    () => [
      {
        key: "existing",
        label: "Existing Patients",
        types: [TClinicPatientType.PATIENT, TClinicPatientType.CONSULT],
        addPatientType: TClinicPatientType.PATIENT,
        addButtonText: "Add a new patient",
        emptyState: {
          title: "No patients yet!",
          description: "Add a new patient to start managing your cases.",
          ctaLabel: "Add a new patient",
          useAddModal: true,
        },
      },
      {
        key: "leads",
        label: "Leads",
        types: [TClinicPatientType.LEAD],
        addPatientType: TClinicPatientType.LEAD,
        addButtonText: "Add a new lead",
        emptyState: {
          title: "No leads yet!",
          description:
            "Integrate our widget on your website to start generating leads through simulations.",
          ctaLabel: "Get Started",
          useAddModal: false,
          onCtaPress: () => {
            window.location.href = "/deploy-widget";
          },
        },
      },
      {
        key: "combined",
        label: "Combined",
        types: [
          TClinicPatientType.PATIENT,
          TClinicPatientType.CONSULT,
          TClinicPatientType.LEAD,
        ],
        addPatientType: TClinicPatientType.PATIENT,
        addButtonText: "Add patient",
        emptyState: {
          title: "No patients or leads yet!",
          description:
            "Deploy your lead gen form to start collecting new leads through simulations.",
          ctaLabel: "Deploy Lead Gen Form",
          useAddModal: false,
          onCtaPress: () => {
            navigate("/deploy-widget");
          },
        },
      },
    ],
    [navigate],
  );

  const filterKeyFromQuery = React.useMemo(() => {
    const filterParam = searchParams.get("filter");
    if (filterParam === "existing" || filterParam === "leads") {
      return filterParam;
    }
    if (filterParam === "combined") return "combined";
    if (leadId) return "leads";
    if (location.pathname.includes("/leads")) return "leads";
    return "combined";
  }, [searchParams, leadId, location.pathname]);

  const [activeFilterKey, setActiveFilterKey] =
    React.useState(filterKeyFromQuery);

  React.useEffect(() => {
    const timeoutId = setTimeout(
      () => setDebouncedSearchTerm(searchTerm.trim()),
      350,
    );
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  React.useEffect(() => {
    setCurrentCursor(undefined);
    setCursorHistory([]);
    setPatientsPagination(null);
  }, [debouncedSearchTerm]);

  React.useEffect(() => {
    setCurrentCursor(undefined);
    setCursorHistory([]);
    setPatientsPagination(null);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setActiveFilterKey(filterKeyFromQuery);
  }, [filterKeyFromQuery]);

  React.useEffect(() => {
    if (!leadId || patientId) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("patientId", leadId);
      next.delete("leadId");
      if (!next.get("filter")) next.set("filter", "leads");
      return next;
    });
  }, [leadId, patientId, setSearchParams]);

  const activeFilter =
    filterOptions.find((option) => option.key === activeFilterKey) ||
    filterOptions[0];

  const storePatientDetail = (patientDetail: TPatientInfo) => {
    setPatientDetail(patientDetail);
  };
  const setPatientDetailToNull = () => {
    setPatientDetail(null);
  };

  const handleStartConsult = React.useCallback(
    (patient: TPatientInfo) => {
      navigate(`/consults?patientId=${encodeURIComponent(patient.uuid)}`, {
        state: {
          selectedPatient: buildConsultPatientFromClinicPatient(patient),
          source: "patient-detail",
        },
      });
    },
    [navigate],
  );

  const handleNextPage = () => {
    if (!patientsPagination?.nextCursor || !patientsPagination.hasMore) return;
    setCursorHistory((prev) => [...prev, currentCursor]);
    setCurrentCursor(patientsPagination.nextCursor);
  };

  const handlePreviousPage = () => {
    if (cursorHistory.length === 0) return;
    const previousCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory((prev) => prev.slice(0, -1));
    setCurrentCursor(previousCursor);
  };

  const addNewPatient = (newPatientInfo: TPatientInfo) => {
    setPatients((prevPatients) => [...prevPatients, newPatientInfo]);
    if (isFreePlan && newPatientInfo.type === TClinicPatientType.LEAD) {
      if (!unlockedFreeLeadUuid) {
        setUnlockedFreeLeadUuid(newPatientInfo.uuid);
      } else {
        setWaitingLeadsCount((prev) => prev + 1);
      }
    }
    setPatientDetail(newPatientInfo);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("patientId", newPatientInfo.uuid);
      return next;
    });
  };

  React.useEffect(() => {
    if (!session || session.planType !== "FREE") {
      setUnlockedFreeLeadUuid(null);
      setWaitingLeadsCount(0);
      return;
    }

    let isMounted = true;

    const fetchFirstLeadUuid = async () => {
      try {
        let oldestLead: TPatientInfo | null = null;
        let totalLeadCount = 0;
        let cursor: string | undefined = undefined;
        let pageCount = 0;

        while (pageCount < MAX_LEAD_LOOKUP_PAGES) {
          const response: TGetPatientsPaginatedResponse =
            await getPatientsPaginated(session, {
              patientTypes: [TClinicPatientType.LEAD],
              limit: LEAD_LOOKUP_PAGE_LIMIT,
              cursor,
            });

          for (const lead of response.patients) {
            if (!oldestLead || isOlderLead(lead, oldestLead)) {
              oldestLead = lead;
            }
          }
          totalLeadCount += response.patients.length;

          const hasMore = Boolean(response.pagination?.hasMore);
          const nextCursor: string | undefined =
            response.pagination?.nextCursor || undefined;

          if (!hasMore || !nextCursor) break;

          cursor = nextCursor;
          pageCount += 1;
        }

        if (isMounted) {
          setUnlockedFreeLeadUuid(oldestLead ? oldestLead.uuid : null);
          setWaitingLeadsCount(
            Math.max(totalLeadCount - (oldestLead ? 1 : 0), 0),
          );
        }
      } catch (error) {
        console.error("Error resolving first lead for free access:", error);
        if (isMounted) {
          setUnlockedFreeLeadUuid(null);
          setWaitingLeadsCount(0);
        }
      }
    };

    fetchFirstLeadUuid();

    return () => {
      isMounted = false;
    };
  }, [session]);

  const handleExportLeadsCsv = React.useCallback(async () => {
    if (!session) return;

    if (session.planType === "FREE") {
      navigate("/upgrade");
      return;
    }

    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    setIsExportingLeadsCsv(true);
    setExportLeadsCsvError(null);

    try {
      let cursor: string | undefined = undefined;
      let pageCount = 0;
      const allLeads: TPatientInfo[] = [];

      while (pageCount < MAX_LEAD_EXPORT_PAGES) {
        const response: TGetPatientsPaginatedResponse =
          await getPatientsPaginated(session, {
            patientTypes: [TClinicPatientType.LEAD],
            limit: LEAD_EXPORT_PAGE_LIMIT,
            cursor,
          });

        allLeads.push(...response.patients);

        const hasMore = Boolean(response.pagination?.hasMore);
        const nextCursor: string | undefined =
          response.pagination?.nextCursor || undefined;

        if (!hasMore || !nextCursor) break;

        cursor = nextCursor;
        pageCount += 1;
      }

      const headers = [
        "lead_uuid",
        "first_name",
        "last_name",
        "email",
        "phone",
        "location",
        "procedures_of_interest",
        "source",
        "created_at",
        "updated_at",
      ];

      const rows = allLeads.map((lead) => [
        lead.uuid || "",
        lead.firstName || "",
        lead.lastName || "",
        lead.email || "",
        lead.phone || "",
        lead.location || "",
        formatCsvProcedures(lead.proceduresOfInterest),
        lead.uploadType || "",
        lead.createdAt || "",
        lead.updatedAt || "",
      ]);

      const csv = [headers, ...rows]
        .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `clinic-leads-${new Date().toISOString().slice(0, 10)}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export leads CSV:", error);
      setExportLeadsCsvError(
        "Unable to export leads right now. Please try again.",
      );
    } finally {
      setIsExportingLeadsCsv(false);
    }
  }, [navigate, session]);

  React.useEffect(() => {
    let isMounted = true;
    const fetchPatients = async () => {
      try {
        if (!session) return;
        setHasFetchedPatients(false);
        setIsErrorGettingPatients(false);

        if (debouncedSearchTerm.length === 1) {
          setPatients([]);
          setPatientsPagination(null);
          return;
        }

        const response = await getPatientsPaginated(session, {
          patientTypes: activeFilter.types,
          ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
          limit: PATIENTS_PAGE_LIMIT,
          cursor: currentCursor,
        });
        if (isMounted) {
          setPatients(response.patients);
          setPatientsPagination(response.pagination);
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
        logUIError({
          message: "Failed to fetch patients list",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          user_email: session?.userEmail || undefined,
          clinic_name: session?.clinicName || undefined,
          clinic_id: session?.clinicId || undefined,
          context: {
            component: "HomePatients",
            activeFilterKey,
            filterTypes: activeFilter.types,
            debouncedSearchTerm,
            error: error instanceof Error ? error.message : String(error),
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in HomePatients", logErr),
        );
        if (isMounted) {
          setIsErrorGettingPatients(true);
          setPatientsPagination(null);
        }
      } finally {
        if (isMounted) setHasFetchedPatients(true);
      }
    };
    fetchPatients();
    return () => {
      isMounted = false;
    };
  }, [
    session,
    activeFilterKey,
    activeFilter.types,
    currentCursor,
    debouncedSearchTerm,
  ]);

  React.useEffect(() => {
    const resolvedPatientId = patientId || leadId;
    if (!session || !resolvedPatientId) return;

    const cachedPatient = patients.find(
      (patient) => patient.uuid === resolvedPatientId,
    );
    if (cachedPatient) {
      setPatientDetail(cachedPatient);
      return;
    }

    let isMounted = true;
    const fetchPatientById = async () => {
      try {
        const response: TGetPatientsResponse = await getPatients(session, {
          patientTypes: [
            TClinicPatientType.PATIENT,
            TClinicPatientType.CONSULT,
            TClinicPatientType.LEAD,
          ],
          patientIds: [resolvedPatientId],
        });
        if (isMounted && response[0]) setPatientDetail(response[0]);
      } catch (error) {
        console.error("Error fetching patient detail:", error);
      }
    };

    fetchPatientById();
    return () => {
      isMounted = false;
    };
  }, [session, patientId, leadId, patients]);

  if (!session) return;

  if (patientDetail) {
    return (
      <PatientDetail
        currentPatientDetail={patientDetail}
        setPatientDetailToNull={setPatientDetailToNull}
        unlockedFreeLeadUuid={unlockedFreeLeadUuid}
        onStartConsult={handleStartConsult}
      />
    );
  }

  return (
    <>
      <View style={styles.titleContainer}>
        <View style={styles.titleFilterContainer}>
          <TitleText
            text="Patients"
            style={{ marginTop: 5 }}
            numberOfLines={1}
          />
          <View
            style={[
              styles.filterDropdown,
              isMobile && styles.filterDropdownMobile,
            ]}
          >
            <InputDropdown
              name=""
              selections={filterOptions.map((option) => option.label)}
              value={activeFilter.label}
              onSelect={(value) => {
                const nextFilter = filterOptions.find(
                  (option) => option.label === value,
                );
                if (!nextFilter) return;
                setCurrentCursor(undefined);
                setCursorHistory([]);
                setPatientsPagination(null);
                setActiveFilterKey(nextFilter.key);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("filter", nextFilter.key);
                  return next;
                });
              }}
            />
          </View>
        </View>
        <View
          style={[styles.headerActions, isMobile && styles.headerActionsMobile]}
        >
          <SecondaryButton
            text={
              isFreePlan
                ? "Export all leads to CSV (paid plans)"
                : isExportingLeadsCsv
                  ? "Exporting..."
                  : "Export all leads to CSV"
            }
            onPress={handleExportLeadsCsv}
            disabled={isExportingLeadsCsv}
            style={styles.exportLeadsButton}
          />
        </View>
      </View>
      {isFreePlan && waitingLeadsCount > 0 ? (
        <View style={styles.waitingLeadsContainer}>
          <NormalText
            text={`You have ${waitingLeadsCount} lead${
              waitingLeadsCount === 1 ? "" : "s"
            } waiting. Upgrade to contact them.`}
            style={styles.waitingLeadsText}
          />
          <PrimaryButton
            text="Upgrade"
            onPress={() => navigate("/upgrade")}
            style={styles.waitingLeadsUpgradeButton}
          />
        </View>
      ) : null}
      {exportLeadsCsvError ? (
        <NormalText text={exportLeadsCsvError} style={styles.exportErrorText} />
      ) : null}
      <View style={styles.listContainer}>
        <PatientList
          storePatientDetail={storePatientDetail}
          patients={patients}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          addNewPatient={addNewPatient}
          isErrorGettingPatients={isErrorGettingPatients}
          hasFetchedPatients={hasFetchedPatients}
          patientType={activeFilter.addPatientType}
          emptyState={activeFilter.emptyState}
          pagination={patientsPagination}
          canGoNext={Boolean(
            patientsPagination?.hasMore && patientsPagination?.nextCursor,
          )}
          canGoPrevious={cursorHistory.length > 0}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          isPaginationLoading={!hasFetchedPatients}
          unlockedFreeLeadUuid={unlockedFreeLeadUuid}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  titleContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 10,
    alignItems: "center",
    justifyContent: "space-between",
    position: "relative",
    zIndex: 20,
  },
  titleFilterContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  filterDropdown: {
    minWidth: 200,
    maxWidth: 260,
    position: "relative",
    zIndex: 30,
    elevation: 10,
  },
  filterDropdownMobile: {
    minWidth: 160,
    maxWidth: 200,
  },
  listContainer: {
    position: "relative",
    zIndex: 1,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerActionsMobile: {
    width: "100%",
    justifyContent: "flex-start",
  },
  exportLeadsButton: {
    minHeight: 44,
    paddingHorizontal: 16,
  },
  waitingLeadsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  waitingLeadsText: {
    color: "#444A57",
  },
  waitingLeadsUpgradeButton: {
    minHeight: 40,
    paddingHorizontal: 16,
  },
  exportErrorText: {
    marginBottom: 10,
    color: "#AA2E25",
  },
});

export default Patients;
